"use client";

import { c as _c } from "react/compiler-runtime";
import { graphql, readFragment } from "@opensea/graphql";
import { Media, TableCell, TableRow, useIsLessThanMd } from "@opensea/ui-kit";
import { CurrencyLink } from "@/components/currencies/CurrencyLink";
import { CurrencyLinkFragment } from "@/components/currencies/CurrencyLink.fragment";
import { CurrencyWatchlistButton, CurrencyWatchlistButtonFragment } from "@/components/currencies/CurrencyWatchlistButton";
import { CurrencyStatsTableRowCollection, CurrencyStatsTableRowCollectionFragment } from "@/components/stats/CurrencyStatsTable/CurrencyStatsTableRowCollection";
import { CurrencyStatsTableRowFDV, CurrencyStatsTableRowFDVFragment } from "@/components/stats/CurrencyStatsTable/CurrencyStatsTableRowFDV";
import { CurrencyStatsTableRowPrice, CurrencyStatsTableRowPriceFragment } from "@/components/stats/CurrencyStatsTable/CurrencyStatsTableRowPrice";
import { CurrencyStatsTableRowSkeleton } from "@/components/stats/CurrencyStatsTable/CurrencyStatsTableRowSkeleton";
import { useIsTokenWatchlistEnabled } from "@/hooks/use-flags";
import { CurrencyStatsTableRowOneDayPriceChange, CurrencyStatsTableRowOneDayPriceChangeFragment } from "./CurrencyStatsTableRowOneDayPriceChange";
import { CurrencyStatsTableRowOneHourPriceChange, CurrencyStatsTableRowOneHourPriceChangeFragment } from "./CurrencyStatsTableRowOneHourPriceChange";
import { CurrencyStatsTableRowPriceMobile, CurrencyStatsTableRowPriceMobileFragment } from "./CurrencyStatsTableRowPriceMobile";
import { CurrencyStatsTableRowSparkLineChart, CurrencyStatsTableRowSparkLineChartFragment } from "./CurrencyStatsTableRowSparkLineChart";
import { CurrencyStatsTableRowThirtyDayPriceChange, CurrencyStatsTableRowThirtyDayPriceChangeFragment } from "./CurrencyStatsTableRowThirtyDayPriceChange";
import { CurrencyStatsTableRowVolume, CurrencyStatsTableRowVolumeFragment } from "./CurrencyStatsTableRowVolume";
import { CURRENCY_STATS_TABLE_COLUMN_CLASSNAMES } from "./constants";
export const CurrencyStatsTableRowFragment = graphql(`
    fragment CurrencyStatsTableRow on Currency {
      id
      contractAddress
      chainIdentifier
      ...CurrencyStatsTableRowFDV
      ...CurrencyStatsTableRowPrice
      ...CurrencyStatsTableRowOneHourPriceChange
      ...CurrencyStatsTableRowOneDayPriceChange
      ...CurrencyStatsTableRowThirtyDayPriceChange
      ...CurrencyStatsTableRowCollection
      ...CurrencyStatsTableRowVolume
      ...CurrencyLink
      ...CurrencyStatsTableRowSparkLineChart
      ...CurrencyStatsTableRowPriceMobile
      ...CurrencyWatchlistButton
    }
  `, [CurrencyStatsTableRowFDVFragment, CurrencyStatsTableRowPriceFragment, CurrencyStatsTableRowOneHourPriceChangeFragment, CurrencyStatsTableRowOneDayPriceChangeFragment, CurrencyStatsTableRowThirtyDayPriceChangeFragment, CurrencyStatsTableRowCollectionFragment, CurrencyStatsTableRowVolumeFragment, CurrencyLinkFragment, CurrencyStatsTableRowSparkLineChartFragment, CurrencyStatsTableRowPriceMobileFragment, CurrencyWatchlistButtonFragment]);
export function CurrencyStatsTableRow(t0) {
  const $ = _c(49);
  const {
    currency: currencyKey,
    preColumns,
    isTokenWatchedMap,
    isTokenWatchedFetching
  } = t0;
  const isLessThanMd = useIsLessThanMd();
  const isTokenWatchlistEnabled = useIsTokenWatchlistEnabled();
  if (!currencyKey) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <CurrencyStatsTableRowSkeleton />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  if ($[1] !== currencyKey) {
    t1 = readFragment(CurrencyStatsTableRowFragment, currencyKey);
    $[1] = currencyKey;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const currency = t1;
  let t2;
  if ($[3] !== currency || $[4] !== isTokenWatchedFetching || $[5] !== isTokenWatchedMap || $[6] !== isTokenWatchlistEnabled) {
    t2 = isTokenWatchlistEnabled ? <TableCell className={CURRENCY_STATS_TABLE_COLUMN_CLASSNAMES.watchlist}><CurrencyWatchlistButton currency={currency} initialState={{
        fetching: isTokenWatchedFetching ?? false,
        isWatched: isTokenWatchedMap?.get(`${currency.chainIdentifier}:${currency.contractAddress}`) ?? false
      }} /></TableCell> : null;
    $[3] = currency;
    $[4] = isTokenWatchedFetching;
    $[5] = isTokenWatchedMap;
    $[6] = isTokenWatchlistEnabled;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const watchlist = t2;
  const t3 = isLessThanMd ? null : watchlist;
  let t4;
  if ($[8] !== currency) {
    t4 = <TableCell className={CURRENCY_STATS_TABLE_COLUMN_CLASSNAMES.currency} stickyLeft={0}><CurrencyStatsTableRowCollection currency={currency} /></TableCell>;
    $[8] = currency;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== currency) {
    t5 = <TableCell className={CURRENCY_STATS_TABLE_COLUMN_CLASSNAMES.stat}><CurrencyStatsTableRowPriceMobile currency={currency} /></TableCell>;
    $[10] = currency;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== currency) {
    t6 = <TableCell className={CURRENCY_STATS_TABLE_COLUMN_CLASSNAMES.lastOneDay}><CurrencyStatsTableRowSparkLineChart currency={currency} /></TableCell>;
    $[12] = currency;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t5 || $[15] !== t6) {
    t7 = <Media lessThan="md">{t5}{t6}</Media>;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== currency) {
    t8 = <TableCell className={CURRENCY_STATS_TABLE_COLUMN_CLASSNAMES.stat}><CurrencyStatsTableRowPrice currency={currency} /></TableCell>;
    $[17] = currency;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== currency) {
    t9 = <TableCell className={CURRENCY_STATS_TABLE_COLUMN_CLASSNAMES.stat}><CurrencyStatsTableRowOneHourPriceChange currency={currency} /></TableCell>;
    $[19] = currency;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t8 || $[22] !== t9) {
    t10 = <Media greaterThanOrEqual="md">{t8}{t9}</Media>;
    $[21] = t8;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== currency) {
    t11 = <TableCell className={CURRENCY_STATS_TABLE_COLUMN_CLASSNAMES.stat}><CurrencyStatsTableRowOneDayPriceChange currency={currency} /></TableCell>;
    $[24] = currency;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== currency) {
    t12 = <TableCell className={CURRENCY_STATS_TABLE_COLUMN_CLASSNAMES.stat}><CurrencyStatsTableRowThirtyDayPriceChange currency={currency} /></TableCell>;
    $[26] = currency;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== currency) {
    t13 = <TableCell className={CURRENCY_STATS_TABLE_COLUMN_CLASSNAMES.stat}><CurrencyStatsTableRowVolume currency={currency} /></TableCell>;
    $[28] = currency;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== currency) {
    t14 = <TableCell className={CURRENCY_STATS_TABLE_COLUMN_CLASSNAMES.stat}><CurrencyStatsTableRowFDV currency={currency} /></TableCell>;
    $[30] = currency;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== currency) {
    t15 = <Media greaterThanOrEqual="md"><TableCell className={CURRENCY_STATS_TABLE_COLUMN_CLASSNAMES.lastOneDay}><CurrencyStatsTableRowSparkLineChart currency={currency} /></TableCell></Media>;
    $[32] = currency;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  const t16 = isLessThanMd ? watchlist : null;
  let t17;
  if ($[34] !== preColumns || $[35] !== t10 || $[36] !== t11 || $[37] !== t12 || $[38] !== t13 || $[39] !== t14 || $[40] !== t15 || $[41] !== t16 || $[42] !== t3 || $[43] !== t4 || $[44] !== t7) {
    t17 = <TableRow interactive={true}>{t3}{t4}{preColumns}{t7}{t10}{t11}{t12}{t13}{t14}{t15}{t16}</TableRow>;
    $[34] = preColumns;
    $[35] = t10;
    $[36] = t11;
    $[37] = t12;
    $[38] = t13;
    $[39] = t14;
    $[40] = t15;
    $[41] = t16;
    $[42] = t3;
    $[43] = t4;
    $[44] = t7;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  let t18;
  if ($[46] !== currency || $[47] !== t17) {
    t18 = <CurrencyLink currency={currency} variant="unstyled">{t17}</CurrencyLink>;
    $[46] = currency;
    $[47] = t17;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  return t18;
}